/**
 * Thought Stream — Copywriter Layer
 *
 * Transforms raw SSE pipeline events (phase + status + data) into
 * human-readable ThoughtEvent objects that the UI renders as a
 * narrated experience instead of developer-oriented log strings.
 *
 * @internal – import from `@/store/useDepartmentStore` instead.
 */

import type { ThoughtEvent, ThoughtEventKind, StepLog } from "./types";
import { humanizeToolCall } from "@/shared/lib/uiMessageUtils";

// ── Helpers ──────────────────────────────────────────────────────────────────

function hostname(url?: string): string {
  if (!url) return "your website";
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function humanizeTrack(track: string): string {
  const map: Record<string, string> = {
    twitter: "Twitter / X",
    perplexity: "market intelligence",
    youtube: "YouTube",
    google_search: "Google Search",
    tiktok: "TikTok",
  };
  return map[track] ?? track;
}

function kChars(chars?: number): string {
  if (!chars) return "your content";
  return `${Math.round(chars / 1000)}k words`;
}

function seconds(ms?: number): string {
  if (!ms) return "a moment";
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.round(s / 60)}m`;
}

// ── Copy type for pipeline maps ──────────────────────────────────────────────

export type ThoughtCopy = Omit<ThoughtEvent, "id" | "ts">;

type CopyFn = (
  data: Record<string, any> | undefined,
  progress?: Record<string, any>,
) => ThoughtCopy;

type PhaseCopyMap = Record<string, Record<string, CopyFn>>;

// ═══════════════════════════════════════════════════════════════════════════════
// Brand Pipeline Copywriter
// ═══════════════════════════════════════════════════════════════════════════════

const BRAND_COPY: PhaseCopyMap = {
  website_scrape: {
    running: (data) => ({
      kind: "searching",
      headline: "Reading your website",
      detail: data?.url ? `Exploring ${hostname(data.url)}...` : undefined,
      source: "web",
    }),
    complete: (data) => ({
      kind: "discovered",
      headline: "Got a feel for your brand voice",
      detail: `Read through ${kChars(data?.chars)} of your content`,
    }),
    skipped: (data) => ({
      kind: "warning",
      headline: "Couldn't reach the website",
      detail: data?.reason ?? "Moving on with what we know",
    }),
  },

  query_generation: {
    running: () => ({
      kind: "thinking",
      headline: "Crafting research questions",
      detail: "Figuring out what to ask the internet about your brand",
    }),
    complete: (data) => ({
      kind: "completed",
      headline: "Research plan ready",
      detail: `Prepared ${data?.query_count ?? "several"} questions across 4 intelligence sources`,
    }),
  },

  collection: {
    running: (_data, progress) => ({
      kind: "searching",
      headline: "Gathering market intelligence",
      detail: progress?.track
        ? `Searching ${humanizeTrack(progress.track)}...`
        : "Pulling data from multiple sources",
      source: progress?.track,
      progress: progress?.total
        ? { current: progress.completed ?? 0, total: progress.total, unit: "sources" }
        : undefined,
    }),
    complete: (data) => ({
      kind: "completed",
      headline: "Intelligence gathered",
      detail: `Collected insights from ${data?.successful ?? "multiple"} sources in ${seconds(data?.duration_ms)}`,
    }),
  },

  distillation: {
    running: () => ({
      kind: "analyzing",
      headline: "Making sense of the data",
      detail: "Distilling key findings from all sources",
    }),
    complete: (data) => ({
      kind: "discovered",
      headline: "Key patterns identified",
      detail: `Extracted ${data?.findings_count ?? "several"} insights across ${data?.sections ?? "multiple"} areas`,
    }),
  },

  synthesis: {
    running: () => ({
      kind: "creating",
      headline: "Synthesizing your brand strategy",
      detail: "Weaving research findings into a cohesive narrative",
    }),
    complete: () => ({
      kind: "completed",
      headline: "Brand strategy ready for review",
      detail: "Your positioning, messaging, and competitive angle are ready",
    }),
  },

  brand_generation: {
    running: () => ({
      kind: "creating",
      headline: "Designing your brand identity",
      detail: "Building palette, typography, and visual system",
    }),
    complete: () => ({
      kind: "completed",
      headline: "Brand identity package ready",
      detail: "Colors, fonts, and brand system are set",
    }),
  },

  logo_generation: {
    idle: () => ({
      kind: "waiting",
      headline: "Ready to create your logo",
      detail: "Hit generate when you want to see AI logo concepts",
    }),
  },

  done: {
    complete: () => ({
      kind: "completed",
      headline: "Brand research complete",
      detail: "All insights are ready — time to review!",
    }),
  },

  error: {
    error: (data) => ({
      kind: "error",
      headline: "Something went wrong",
      detail: data?.message ?? "The pipeline encountered an issue",
    }),
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Research Pipeline Copywriter
// ═══════════════════════════════════════════════════════════════════════════════

const RESEARCH_COPY: PhaseCopyMap = {
  prompt_generation: {
    running: () => ({
      kind: "thinking",
      headline: "Designing your research plan",
      detail: "Generating targeted search queries for your brand",
    }),
    complete: (data) => ({
      kind: "completed",
      headline: "Research plan locked in",
      detail: `${data?.query_count ?? "Multiple"} queries ready across search engines and AI`,
    }),
  },
  data_collection: {
    running: (_data, progress) => ({
      kind: "searching",
      headline: "Scouring the web for insights",
      detail: progress?.track
        ? `Searching ${humanizeTrack(progress.track)}...`
        : "Collecting data from multiple engines",
      source: progress?.track,
      progress: progress?.total
        ? { current: progress.completed ?? 0, total: progress.total, unit: "queries" }
        : undefined,
    }),
    complete: (data) => ({
      kind: "completed",
      headline: "Data collection done",
      detail: `${data?.successful ?? "Many"} results gathered in ${seconds(data?.duration_ms)}`,
    }),
  },
  distillation: {
    running: () => ({
      kind: "analyzing",
      headline: "Analyzing what we found",
      detail: "Cross-referencing sources to extract key insights",
    }),
    complete: (data) => ({
      kind: "discovered",
      headline: "Research insights distilled",
      detail: `${data?.findings_count ?? "Key"} findings identified`,
    }),
  },
  synthesis: {
    running: () => ({
      kind: "creating",
      headline: "Building your strategic overview",
      detail: "Turning raw research into actionable intelligence",
    }),
    complete: () => ({
      kind: "completed",
      headline: "Campaign strategy synthesized",
    }),
  },
  geo_plan: {
    running: () => ({ kind: "creating", headline: "Mapping geographic opportunities" }),
    complete: () => ({ kind: "completed", headline: "GEO plan ready" }),
  },
  ads_plan: {
    running: () => ({ kind: "creating", headline: "Crafting your ad strategy" }),
    complete: () => ({ kind: "completed", headline: "Ad plan ready" }),
  },
  content_plan: {
    running: () => ({ kind: "creating", headline: "Building your content playbook" }),
    complete: () => ({ kind: "completed", headline: "Content plan ready" }),
  },
  done: {
    complete: () => ({
      kind: "completed",
      headline: "Research complete",
      detail: "Full campaign strategy is ready for review",
    }),
  },
  error: {
    error: (data) => ({
      kind: "error",
      headline: "Research hit a snag",
      detail: data?.message ?? "Something went wrong during analysis",
    }),
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Social Pipeline Copywriter
// ═══════════════════════════════════════════════════════════════════════════════

const SOCIAL_COPY: PhaseCopyMap = {
  query_generation: {
    running: () => ({ kind: "thinking", headline: "Planning social media research" }),
    complete: (data) => ({
      kind: "completed",
      headline: "Social research plan set",
      detail: `${data?.query_count ?? "Several"} queries across platforms`,
    }),
  },
  collection: {
    running: (_data, progress) => ({
      kind: "searching",
      headline: "Scanning social platforms",
      detail: progress?.track ? `Exploring ${humanizeTrack(progress.track)}...` : undefined,
      source: progress?.track,
      progress: progress?.total
        ? { current: progress.completed ?? 0, total: progress.total, unit: "sources" }
        : undefined,
    }),
    complete: (data) => ({
      kind: "completed",
      headline: "Social data collected",
      detail: data?.videos_collected ? `Found ${data.videos_collected} videos` : undefined,
    }),
  },
  trend_analysis: {
    running: () => ({ kind: "analyzing", headline: "Spotting social trends" }),
    complete: () => ({ kind: "discovered", headline: "Trend patterns identified" }),
  },
  competitor_audit: {
    running: () => ({ kind: "analyzing", headline: "Auditing competitor strategies" }),
    complete: () => ({ kind: "discovered", headline: "Competitor insights ready" }),
  },
  template_generation: {
    running: () => ({ kind: "creating", headline: "Building content templates" }),
    complete: () => ({ kind: "completed", headline: "Template library ready" }),
  },
  calendar_generation: {
    running: () => ({ kind: "creating", headline: "Drafting your social calendar" }),
    complete: () => ({ kind: "completed", headline: "Social calendar ready" }),
  },
  done: {
    complete: () => ({ kind: "completed", headline: "Social strategy complete" }),
  },
  error: {
    error: (data) => ({
      kind: "error",
      headline: "Social research failed",
      detail: data?.message ?? undefined,
    }),
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Growth Pipeline Copywriter
// ═══════════════════════════════════════════════════════════════════════════════

const GROWTH_COPY: PhaseCopyMap = {
  website_analysis: {
    running: () => ({ kind: "searching", headline: "Analyzing your website", source: "web" }),
    complete: (data) => ({
      kind: "discovered",
      headline: "Website structure mapped",
      detail: data?.pages ? `Found ${data.pages.length} key pages` : undefined,
    }),
  },
  tracking_plan: {
    running: () => ({ kind: "creating", headline: "Designing tracking events" }),
    complete: (data) => ({
      kind: "completed",
      headline: "Tracking plan ready",
      detail: data?.event_count ? `${data.event_count} events configured` : undefined,
    }),
  },
  provisioning: {
    running: () => ({ kind: "creating", headline: "Setting up Google Marketing tools" }),
    complete: () => ({ kind: "completed", headline: "Google accounts provisioned" }),
  },
  campaign_setup: {
    running: () => ({ kind: "creating", headline: "Building your campaigns" }),
    complete: () => ({ kind: "completed", headline: "Campaigns configured" }),
  },
  done: {
    complete: () => ({ kind: "completed", headline: "Growth stack ready to launch" }),
  },
  error: {
    error: (data) => ({
      kind: "error",
      headline: "Growth pipeline issue",
      detail: data?.message ?? undefined,
    }),
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// GEO Pipeline Copywriter
// ═══════════════════════════════════════════════════════════════════════════════

const GEO_COPY: PhaseCopyMap = {
  site_scan: {
    running: () => ({
      kind: "searching",
      headline: "Scanning your site for GEO signals",
      source: "web",
    }),
    complete: () => ({ kind: "discovered", headline: "Site scan complete" }),
  },
  ai_citations: {
    running: () => ({ kind: "searching", headline: "Checking AI citation coverage" }),
    complete: (data) => ({
      kind: "discovered",
      headline: "AI citation audit done",
      detail: data?.citation_count ? `Found ${data.citation_count} AI mentions` : undefined,
    }),
  },
  scoring: {
    running: () => ({ kind: "analyzing", headline: "Calculating your GEO score" }),
    complete: () => ({ kind: "completed", headline: "GEO scorecard ready" }),
  },
  content_gaps: {
    running: () => ({ kind: "analyzing", headline: "Finding content gaps" }),
    complete: (data) => ({
      kind: "discovered",
      headline: "Content gaps identified",
      detail: data?.gap_count ? `${data.gap_count} opportunities found` : undefined,
    }),
  },
  asset_generation: {
    running: () => ({ kind: "creating", headline: "Building SEO/GEO assets" }),
    complete: () => ({ kind: "completed", headline: "Assets ready for review" }),
  },
  done: {
    complete: () => ({ kind: "completed", headline: "GEO audit complete" }),
  },
  error: {
    error: (data) => ({
      kind: "error",
      headline: "GEO audit issue",
      detail: data?.message ?? undefined,
    }),
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Ecommerce Pipeline Copywriter
// ═══════════════════════════════════════════════════════════════════════════════

const ECOMMERCE_COPY: PhaseCopyMap = {
  // ── Store connection ──
  shopify_connect: {
    running: (data) => ({
      kind: "searching",
      headline: "Connecting to your Shopify store",
      detail: data?.shopDomain
        ? `Validating access to ${data.shopDomain}`
        : "Validating store credentials and pulling metadata",
    }),
    complete: (data) => ({
      kind: "completed",
      headline: "Store connected",
      detail: data?.shopName
        ? `${data.shopName} (${data.shopCurrency ?? "USD"}) — store plan and settings loaded`
        : "Store metadata loaded",
    }),
  },
  manual_store_created: {
    complete: (data) => ({
      kind: "completed",
      headline: "Manual store created",
      detail: data?.shopName ? `Workspace set up for ${data.shopName}` : undefined,
    }),
  },

  // ── Product import ──
  product_import: {
    running: (_data, progress) => ({
      kind: "searching",
      headline: "Pulling your product catalog",
      detail: "Fetching products, variants, pricing, and inventory from Shopify",
      progress: progress?.total
        ? { current: progress.completed ?? 0, total: progress.total, unit: "products" }
        : undefined,
    }),
    complete: (data) => ({
      kind: "discovered",
      headline: "Catalog loaded",
      detail: data?.count
        ? `${data.count} products with images, variants, and stock levels`
        : "Product data ready",
    }),
  },
  catalog_summary: {
    complete: (data) => ({
      kind: "discovered",
      headline: "Catalog snapshot ready",
      detail: data?.activeProducts
        ? `${data.activeProducts} active products — price range $${data.priceMin ?? 0}–$${data.priceMax ?? 0}`
        : "Active products, price range, and tag analysis computed",
    }),
  },

  // ── File extraction (manual upload) ──
  file_schema_detection: {
    running: () => ({
      kind: "analyzing",
      headline: "Reading your file",
      detail: "Identifying columns, headers, and how your data is organized",
    }),
    complete: () => ({
      kind: "discovered",
      headline: "File structure understood",
      detail: "We know how your file is laid out — extracting products next",
    }),
  },
  file_product_extraction: {
    running: () => ({
      kind: "searching",
      headline: "Pulling products from your file",
      detail: "Grabbing product names, prices, costs, and other details",
    }),
    complete: (data) => ({
      kind: "discovered",
      headline: "Products ready",
      detail: data?.count
        ? `Found ${data.count} products in your file`
        : "All products pulled from your file",
    }),
  },

  // ── Brand analysis ──
  brand_analysis: {
    running: () => ({
      kind: "analyzing",
      headline: "Studying your brand DNA",
      detail: "Analyzing catalog to map voice, positioning, and competitive angle",
    }),
    complete: (data) => ({
      kind: "discovered",
      headline: "Brand profile mapped",
      detail: data?.pricePositioning
        ? `${data.pricePositioning} positioning — USPs, demographics, and market opportunities identified`
        : "Brand voice, target demographic, and competitive position identified",
    }),
  },

  // ── Product categorization ──
  categorization: {
    running: () => ({
      kind: "analyzing",
      headline: "Assigning ad categories and hooks",
      detail: "Mapping each product to Google taxonomy with tailored ad angles",
    }),
    complete: (data) => ({
      kind: "completed",
      headline: "Products categorized for ads",
      detail: data?.count
        ? `${data.count} products tagged with taxonomy, ad hooks, and target keywords`
        : "Each product tagged with taxonomy, ad angle, and target keywords",
    }),
  },

  // ── Order & metrics ──
  fetch_orders: {
    running: () => ({
      kind: "searching",
      headline: "Pulling order history",
      detail: "Fetching recent orders and customer data from Shopify",
    }),
    complete: (data) => ({
      kind: "discovered",
      headline: "Order data collected",
      detail: data?.orderCount
        ? `${data.orderCount} orders loaded`
        : "Order history ready for analysis",
    }),
  },
  unit_economics: {
    running: () => ({
      kind: "analyzing",
      headline: "Computing store-level economics",
      detail: "Calculating AOV, repeat purchase rate, LTV, and margin waterfall",
    }),
    complete: (data) => ({
      kind: "completed",
      headline: "Unit economics computed",
      detail: data?.avg_order_value
        ? `AOV $${data.avg_order_value} — margins, LTV, and refund rates calculated`
        : "AOV, margins, LTV, and refund rates ready",
    }),
  },

  // ── Priority scoring ──
  scoring: {
    running: () => ({
      kind: "analyzing",
      headline: "Scoring each product's ad potential",
      detail: "Computing breakeven CPA, margin waterfall, and ROI verdict per product",
    }),
    complete: (data) => ({
      kind: "completed",
      headline: "Ad-readiness scored",
      detail: data?.viable
        ? `${data.viable} products viable for ads — ranked by ROI potential`
        : "Products ranked with breakeven thresholds and ad-readiness verdicts",
    }),
  },

  // ── Ad cost estimation ──
  ad_cost_estimation: {
    running: () => ({
      kind: "searching",
      headline: "Researching actual ad costs for your market",
      detail: "Looking up current rates across Google, Meta, and TikTok",
    }),
    complete: (data) => ({
      kind: "completed",
      headline: "Ad cost research complete",
      detail: data?.count
        ? `Modeled expected acquisition costs for ${data.count} products`
        : "Expected acquisition costs modeled across all major platforms",
    }),
  },
  ad_cost_google: {
    running: () => ({
      kind: "searching",
      headline: "Analyzing Google Search costs",
      detail: "Reviewing search volume and competitor bidding",
      source: "google_search",
    }),
  },
  ad_cost_meta: {
    running: () => ({
      kind: "searching",
      headline: "Estimating Facebook & Instagram ad costs",
      detail: "Evaluating audience reach and expected conversion rates",
    }),
  },
  ad_cost_tiktok: {
    running: () => ({
      kind: "searching",
      headline: "Checking TikTok bid suggestions",
      detail: "Looking at viral reach potential and cost-per-view",
      source: "tiktok",
    }),
  },
  weighted_cpa: {
    complete: () => ({
      kind: "discovered",
      headline: "True cost-per-acquisition modeled",
      detail: "Combined data from all platforms to find realistic targets",
    }),
  },

  // ── Portfolio synthesis ──
  portfolio_synthesis: {
    running: () => ({
      kind: "creating",
      headline: "Crafting your ultimate ad budget strategy",
      detail: "Finding the best products to advertise and planning your spend",
    }),
    complete: () => ({
      kind: "completed",
      headline: "Ad strategy complete",
      detail: "Your customized budget plan and platform recommendations are ready",
    }),
  },
  portfolio_ranking: {
    running: () => ({
      kind: "analyzing",
      headline: "Finding your most profitable products",
      detail: "Highlighting items with the best profit margins for ads",
    }),
  },
  budget_scenarios: {
    running: () => ({
      kind: "creating",
      headline: "Simulating different ad budgets",
      detail: "Predicting your return on ad spend at various monthly levels",
    }),
  },
  strategic_insights: {
    running: () => ({
      kind: "creating",
      headline: "Finalizing your game plan",
      detail: "AI is writing out your step-by-step launch recommendations",
    }),
    complete: () => ({
      kind: "discovered",
      headline: "Game plan ready",
      detail: "Strategic recommendations and budget scenarios are finalized",
    }),
  },

  done: {
    complete: () => ({
      kind: "completed",
      headline: "E-commerce analysis complete",
      detail: "Full intelligence package ready for review",
    }),
  },
  error: {
    error: (data) => ({
      kind: "error",
      headline: "Analysis hit a snag",
      detail: data?.message ?? "Something went wrong — please try again",
    }),
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Content Intelligence Copywriter
// ═══════════════════════════════════════════════════════════════════════════════

const CONTENT_INTELLIGENCE_COPY: PhaseCopyMap = {
  keyword_generation: {
    running: () => ({ kind: "analyzing", headline: "Identifying ideal target audience" }),
    complete: () => ({
      kind: "completed",
      headline: "Audience mapped",
      detail: "Found the perfect angles and keywords for your products",
    }),
  },
  meta_discovery: {
    running: () => ({
      kind: "searching",
      headline: "Scouting competitor Meta ads",
      source: "meta",
    }),
    complete: (data) => ({
      kind: "discovered",
      headline: "Meta ad strategy formulated",
      detail: data?.metaTopAds?.length
        ? `Found ${data.metaTopAds.length} winning ad formats on Facebook & Instagram`
        : "Found the best performing hooks and visuals",
      source: "meta",
    }),
  },
  tiktok_discovery: {
    running: () => ({
      kind: "searching",
      headline: "Scanning TikTok for viral trends",
      source: "tiktok",
    }),
    complete: (data) => ({
      kind: "discovered",
      headline: "TikTok trends gathered",
      detail: data?.topAds?.length
        ? `Gathered ${data.topAds.length} high-performing videos in your niche`
        : "Gathered top performing videos in your niche",
      source: "tiktok",
    }),
  },
  video_analysis: {
    running: (_data, progress) => ({
      kind: "analyzing",
      headline: "Watching and learning from top videos",
      progress: progress?.total
        ? { current: progress.completed ?? 0, total: progress.total, unit: "videos" }
        : undefined,
    }),
    complete: () => ({
      kind: "discovered",
      headline: "Creative patterns decoded",
      detail: "Understood exactly why these videos get views and engagement",
    }),
  },
  content_synthesis: {
    running: () => ({
      kind: "creating",
      headline: "Drafting your custom ad scripts",
      detail: "Bringing together all insights to write perfect ad copy",
    }),
    complete: (data) => ({
      kind: "completed",
      headline: "Content briefs ready",
      detail: data?.contentBriefs?.length
        ? `${data.contentBriefs.length} ready-to-produce scripts generated`
        : "Scripts and visual directions are ready for production",
    }),
  },
  done: {
    complete: () => ({ kind: "completed", headline: "Content intelligence complete" }),
  },
  error: {
    error: (data) => ({
      kind: "error",
      headline: "Analysis hit a snag",
      detail: data?.message ?? "Something went wrong, please try again",
    }),
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Public API
// ═══════════════════════════════════════════════════════════════════════════════

type PipelineId =
  | "brand"
  | "research"
  | "social"
  | "growth"
  | "geo"
  | "ecommerce"
  | "content-intelligence";

const PIPELINE_MAPS: Record<PipelineId, PhaseCopyMap> = {
  brand: BRAND_COPY,
  research: RESEARCH_COPY,
  social: SOCIAL_COPY,
  growth: GROWTH_COPY,
  geo: GEO_COPY,
  ecommerce: ECOMMERCE_COPY,
  "content-intelligence": CONTENT_INTELLIGENCE_COPY,
};

/**
 * Convert a raw SSE event into a human-readable ThoughtEvent (minus id/ts).
 * Returns `null` if no copy is defined for this phase+status combination,
 * in which case the caller should fall back to the raw log only.
 */
export function toThoughtEvent(
  pipeline: PipelineId,
  phase: string,
  status: string,
  data?: Record<string, any>,
  progress?: Record<string, any>,
): ThoughtCopy | null {
  const map = PIPELINE_MAPS[pipeline];
  if (!map) return null;
  const phaseMap = map[phase];
  if (!phaseMap) return null;
  const fn = phaseMap[status];
  if (!fn) return null;
  return fn(data, progress);
}

/**
 * Convert legacy StepLog entries into ThoughtEvents for backwards compatibility.
 * Used when `thoughtEvents` is empty but `stepLogs` has data (e.g. existing sessions).
 */
export function stepLogsToThoughtEvents(logs: StepLog[]): ThoughtEvent[] {
  return logs.map((log) => {
    let kind: ThoughtEventKind = "thinking";
    let headline = log.text;

    // Parse the bracketed prefix pattern: [SUCCESS], [ERROR], [WARN], etc.
    const prefixMatch = log.text.match(/^\[([A-Z_ ]+)\]\s*(.*)$/);
    if (prefixMatch) {
      const prefix = prefixMatch[1];
      const body = prefixMatch[2];
      headline = body || log.text;

      switch (prefix) {
        case "SUCCESS":
          kind = "completed";
          break;
        case "ERROR":
          kind = "error";
          break;
        case "WARN":
          kind = "warning";
          break;
        case "ACTION REQUIRED":
        case "WAIT":
        case "READY":
          kind = "waiting";
          break;
        case "CONFIRMED":
          kind = "completed";
          break;
        case "TOOL":
          kind = "creating";
          break;
        case "TOOL:RESULT":
          kind = "completed";
          break;
        case "NODE":
          kind = "creating";
          break;
        default:
          kind = "thinking";
      }
    } else if (
      log.text.toLowerCase().startsWith("scraping") ||
      log.text.toLowerCase().startsWith("starting")
    ) {
      kind = "searching";
    }

    // Parse tool-prefixed log lines and map them to the same labels used in chat cards.
    // Examples:
    // [TOOL] generate_ads_image · ...
    // [TOOL:external] generate_ads_video
    // [TOOL:RESULT] generate_ads_video output available
    const prefixedToolMatch = log.text.match(
      /^\[(TOOL(?::[^\]]+)?|TOOL:RESULT)\]\s*([a-zA-Z0-9_:-]+)(?:\s*[·|].*)?$/,
    );
    if (prefixedToolMatch) {
      const toolPrefix = prefixedToolMatch[1];
      const toolName = prefixedToolMatch[2];
      const human = humanizeToolCall(toolName, null) ?? toolName;
      kind = toolPrefix === "TOOL:RESULT" ? "completed" : "creating";
      headline = human;
    }

    // Nested tool result format:
    // [ERROR] [TOOL:RESULT] generate_ads_video output error
    const nestedToolResultMatch = headline.match(/^\[TOOL:RESULT\]\s*([a-zA-Z0-9_:-]+)/);
    if (nestedToolResultMatch) {
      const toolName = nestedToolResultMatch[1];
      const human = humanizeToolCall(toolName, null) ?? toolName;
      headline = kind === "error" ? `${human} failed` : human;
    }

    if (headline.includes("ads_open_new_ad")) {
      headline = "Starting a new ad";
      kind = "thinking";
    }

    return {
      id: log.id,
      ts: log.ts,
      kind,
      headline,
      rawLog: log.text,
    };
  });
}

/**
 * Get thought events for a step, falling back to converted stepLogs if empty.
 */
export function getThoughtEventsWithFallback(
  thoughtEvents: Record<number, ThoughtEvent[]>,
  stepLogs: Record<number, StepLog[]>,
  stepIndex: number,
): ThoughtEvent[] {
  const events = thoughtEvents[stepIndex];
  if (events && events.length > 0) return events;

  // Fallback: convert step logs into thought events
  const logs = stepLogs[stepIndex];
  if (logs && logs.length > 0) return stepLogsToThoughtEvents(logs);

  return [];
}

/**
 * Kind → visual config mapping for the renderer.
 */
export const THOUGHT_KIND_CONFIG: Record<
  ThoughtEventKind,
  {
    icon: string; // Lucide icon name
    color: string; // Tailwind color class (text-*)
    bgColor: string; // Tailwind bg color class
    pulse: boolean; // Whether to animate
  }
> = {
  thinking: {
    icon: "Sparkles",
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    pulse: true,
  },
  searching: { icon: "Globe", color: "text-blue-400", bgColor: "bg-blue-500/10", pulse: true },
  analyzing: {
    icon: "Microscope",
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/10",
    pulse: false,
  },
  creating: { icon: "Wand2", color: "text-violet-400", bgColor: "bg-violet-500/10", pulse: false },
  discovered: {
    icon: "Lightbulb",
    color: "text-amber-400",
    bgColor: "bg-amber-500/10",
    pulse: false,
  },
  completed: {
    icon: "CheckCircle2",
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    pulse: false,
  },
  waiting: { icon: "Hand", color: "text-amber-400", bgColor: "bg-amber-500/10", pulse: true },
  warning: {
    icon: "AlertTriangle",
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
    pulse: false,
  },
  error: { icon: "XCircle", color: "text-red-400", bgColor: "bg-red-500/10", pulse: false },
};
